import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { formatPrice } from '@/lib/format'
import { useTheme } from '@/lib/hooks'

const PAYMENT_TYPES = ['card', 'cash', 'contactless']
const PRODUCT_NAMES = [
  'Flowflex Covid-19 Test Kit',
  'WellO2 MyBreath Smart',
  'StylPro Thermal Belt',
  'Nicorette Quickmist',
  'E45 Moisturiser Cream',
  'Aveeno Daily Moisturising',
]

const generateOrders = () => {
  const orders = []
  const now = new Date()

  for (let i = 0; i < 10; i++) {
    const itemCount = Math.floor(Math.random() * 4) + 1
    const items = []
    for (let j = 0; j < itemCount; j++) {
      const qty = Math.floor(Math.random() * 3) + 1
      const price = parseFloat((Math.random() * 50 + 5).toFixed(2))
      items.push({
        name: PRODUCT_NAMES[Math.floor(Math.random() * PRODUCT_NAMES.length)],
        qty,
        price,
      })
    }
    const total = items.reduce((sum, item) => sum + item.qty * item.price, 0)
    const isCancelled = i === 7 // 1 cancelled
    const time = new Date(now.getTime() - (i + 1) * 1000 * 60 * Math.floor(Math.random() * 15 + 5))

    orders.push({
      ts: time.getTime(),
      time: time.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
      items,
      total: parseFloat(total.toFixed(2)),
      paymentType: isCancelled ? '—' : PAYMENT_TYPES[Math.floor(Math.random() * PAYMENT_TYPES.length)],
      status: isCancelled ? 'cancelled' : 'paid',
    })
  }

  // Newest first; IDs follow time order so the newest has the highest number
  return orders
    .sort((a, b) => a.ts - b.ts)
    .map((order, i) => ({ ...order, id: `TXN-${String(1000 + i).slice(1)}` }))
    .reverse()
}

const TransactionsTab = () => {
  const { t } = useTranslation()
  const { currency } = useTheme()
  const orders = useMemo(() => generateOrders(), [])

  return (
    <Wrapper>
      <SectionTitle>{t('transactionsTab.title')}</SectionTitle>
      <TableCard>
        <Table>
          <thead>
            <tr>
              <Th>{t('transactionsTab.columns.id')}</Th>
              <Th>{t('transactionsTab.columns.time')}</Th>
              <Th align="right">{t('transactionsTab.columns.items')}</Th>
              <Th align="right">{t('transactionsTab.columns.total')}</Th>
              <Th>{t('transactionsTab.columns.payment')}</Th>
              <Th>{t('transactionsTab.columns.status')}</Th>
            </tr>
          </thead>
          <tbody>
            {orders.map(order => (
              <Tr key={order.id} $cancelled={order.status === 'cancelled'}>
                <Td $muted>{order.id}</Td>
                <Td>{order.time}</Td>
                <Td align="right">{order.items.reduce((s, i) => s + i.qty, 0)}</Td>
                <Td align="right" $amount>{formatPrice(order.total, currency)}</Td>
                <Td>{order.paymentType === '—' ? '—' : t(`transactionsTab.paymentType.${order.paymentType}`)}</Td>
                <Td>
                  <StatusBadge status={order.status}>{t(`transactionsTab.status.${order.status}`)}</StatusBadge>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </TableCard>
    </Wrapper>
  )
}

export default TransactionsTab

const Wrapper = styled.div`
  padding: 4px 0 8px;
`

const SectionTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  margin: 4px 0 12px;
  color: ${({ theme }) => theme.colors.global};
`

const TableCard = styled.div`
  background: #ffffff;
  border: 1px solid var(--color-line);
  border-radius: var(--radius-card);
  overflow: hidden;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;

  tbody tr + tr td {
    border-top: 1px solid #EEF0F3;
  }
`

const Th = styled.th`
  text-align: ${({ align }) => align || 'left'};
  padding: 12px ${({ align }) => align === 'right' ? '24px' : '16px'} 12px 16px;
  font-size: 0.8125rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.muted};
  background: #FAFBFC;
  border-bottom: 1px solid var(--color-line);
`

const Tr = styled.tr`
  ${({ $cancelled, theme }) => $cancelled && `
    td {
      color: ${theme.colors.muted};
    }
    td:nth-child(4) {
      text-decoration: line-through;
      font-weight: 500;
    }
  `}
`

const Td = styled.td`
  height: 52px;
  padding: 0 ${({ align }) => align === 'right' ? '24px' : '16px'} 0 16px;
  font-size: 0.92rem;
  text-align: ${({ align }) => align || 'left'};
  white-space: nowrap;
  font-weight: ${({ $amount }) => $amount ? '700' : '400'};
  color: ${({ $muted, theme }) => $muted ? theme.colors.muted : theme.colors.global};
`

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 4px 8px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.2;
  background: ${({ status }) => status === 'paid' ? 'var(--color-success-soft)' : '#FCEDEC'};
  color: ${({ status }) => status === 'paid' ? 'var(--color-success)' : '#B3362B'};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`
